import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQuery, createApiErrorTransformer } from '@/lib/api/baseQuery';
import {
  AMCData,
  CATEGORY_COLORS,
  ChartData,
  DashboardData,
  HoldingData,
  MultipleBenchmarkRequest,
  MultipleBenchmarkResponse,
  PMSBenchmarkReturnResponse,
  PMSCategorySummary,
  PMSCategorySummaryResponse,
  PMSDashboardData,
  PMSDashboardResponse,
  PMSHoldingRow,
  PMSHoldingsPagedResponse,
  PMSPortfolioPerformanceResponse,
  PMSPortfolioSummaryAPResponse,
  PMSPortfolioSummaryResponse,
  PMSSchemeDetailResponse,
  PMSSchemesPagedResponse,
  PMSSchemeSuggestionsResponse,
  PMSSectorHolding,
  PMSSectorHoldingsResponse,
  PMSTransactionsPagedResponse,
  SECTOR_COLORS,
  SECURITY_TYPE_COLORS,
  TopCompanyHoldingsResponse,
  TopSectorHoldingsResponse,
} from '@/types/wealth/pms';

const PMS_API_URL = import.meta.env.VITE_PMS_URL;

// PMS API using RTK Query
export const pmsApi = createApi({
  reducerPath: 'pmsApi',
  baseQuery: createBaseQuery(PMS_API_URL),
  tagTypes: ['Portfolio', 'Schemes'],
  endpoints: (builder) => ({
    getClientPortfolioSummary: builder.query<
      PMSPortfolioSummaryResponse,
      { clientCode: string; source?: string }
    >({
      query: ({ clientCode, source = 'ap' }) => ({
        url: '/v1/clients/get-client-portfolio-summary',
        method: 'GET',
        params: {
          source,
          clientCode,
        },
      }),
      transformErrorResponse: createApiErrorTransformer('PmsGetClientPortfolioSummary', true),
      providesTags: ['Portfolio'],
    }),

    getClientHoldings: builder.query<
      PMSHoldingsPagedResponse,
      {
        clientCode: string;
        source?: string;
        pageNumber?: number;
        pageSize?: number;
      }
    >({
      query: ({ clientCode, source = 'ap', pageNumber = 1, pageSize = 10 }) => ({
        url: '/v1/clients/connect/get-client-holding',
        method: 'GET',
        params: {
          source,
          clientCode,
          pageNumber,
          pageSize,
        },
      }),
      transformResponse: (response: any): PMSHoldingsPagedResponse => {
        const payload = response?.data ?? response;
        const raw = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : [];

        const toNumber = (v: any): number => {
          if (v === null || v === undefined || v === '') return 0;
          const n = typeof v === 'string' ? Number(v.toString().replace(/[,\s]/g, '')) : Number(v);
          return isNaN(n) ? 0 : n;
        };

        const mapped: PMSHoldingRow[] = raw.map(
          (r: any): PMSHoldingRow => ({
            pan_no: r?.pan_no ?? '',
            isin: r?.isin ?? '',
            holding_date: r?.holding_date ?? '',
            security_type: r?.security_type ?? '',
            company_name: r?.company_name ?? null,
            category: r?.category ?? null,
            sector: r?.sector ?? null,
            valuation_date: r?.valuation_date ?? '',
            portfolio_weightage: toNumber(r?.portfolio_weightage),
            portfolio_value: toNumber(r?.portfolio_value),
          }),
        );

        return {
          success: true,
          data: {
            pageNumber: Number(payload?.pageNumber ?? 1),
            pageSize: Number(payload?.pageSize ?? mapped.length),
            totalCount: Number(payload?.totalCount ?? mapped.length),
            data: mapped,
          },
          message: response?.message || undefined,
        };
      },
      transformErrorResponse: createApiErrorTransformer('PmsGetClientHoldings', true),
      providesTags: ['Portfolio'],
    }),

    getClientSectorHoldings: builder.query<
      PMSSectorHoldingsResponse,
      { clientCode: string; source?: string }
    >({
      query: ({ clientCode, source = 'ap' }) => ({
        url: '/v1/clients/get-client-portfolio-sector-holdings',
        method: 'GET',
        params: {
          source,
          clientCode,
        },
      }),
      transformResponse: (response: any): PMSSectorHoldingsResponse => {
        const arr: any[] = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        const toNumber = (v: any): number => {
          if (v === null || v === undefined || v === '') return 0;
          const n = typeof v === 'string' ? Number(v.toString().replace(/[,\s]/g, '')) : Number(v);
          return isNaN(n) ? 0 : n;
        };

        const mapped: PMSSectorHolding[] = arr.map((r) => ({
          pan_no: r?.pan_no ?? '',
          key: r?.key ?? '',
          average: toNumber(r?.average),
        }));

        return { success: true, data: mapped, message: response?.message };
      },
      transformErrorResponse: createApiErrorTransformer('PmsGetClientSectorHoldings', true),
      providesTags: ['Portfolio'],
    }),

    getClientCategorySummary: builder.query<
      PMSCategorySummaryResponse,
      { clientCode: string; source?: string }
    >({
      query: ({ clientCode, source = 'ap' }) => ({
        url: '/v1/clients/get-client-category-summary',
        method: 'GET',
        params: {
          source,
          clientCode,
        },
      }),
      transformResponse: (response: any): PMSCategorySummaryResponse => {
        const arr: any[] = Array.isArray(response?.data) ? response.data : [];
        const toNumber = (v: any): number => {
          if (v === null || v === undefined || v === '') return 0;
          const n = typeof v === 'string' ? Number(v.toString().replace(/[,\s]/g, '')) : Number(v);
          return isNaN(n) ? 0 : n;
        };

        const mapped: PMSCategorySummary[] = arr.map((r) => ({
          key: r?.key ?? '',
          average: toNumber(r?.average),
        }));

        return {
          statusCode: response?.statusCode || 200,
          data: mapped,
          message: response?.message || 'Category summary fetched',
          success: response?.success ?? true,
        };
      },
      transformErrorResponse: createApiErrorTransformer('PmsGetClientCategorySummary', true),
      providesTags: ['Portfolio'],
    }),

    getSchemeBenchmarkReturns: builder.query<
      PMSBenchmarkReturnResponse,
      { schemeId: number | string; clientCode?: string; source?: string }
    >({
      query: ({ schemeId, clientCode, source = 'ap' }) => ({
        url: '/v1/scheme/get-scheme-benchmarks-return-data',
        method: 'GET',
        params: {
          schemeId,
          source,
          ...(clientCode ? { clientCode } : {}),
        },
      }),
      transformResponse: (response: any): PMSBenchmarkReturnResponse => {
        return {
          statusCode: response?.statusCode || 200,
          data: response?.data || {},
          message: response?.message || 'Benchmark returns fetched',
          success: response?.success ?? true,
        };
      },
      transformErrorResponse: createApiErrorTransformer('PmsGetSchemeBenchmarkReturns', true),
      providesTags: ['Portfolio'],
    }),

    getMultipleBenchmarkData: builder.query<MultipleBenchmarkResponse, MultipleBenchmarkRequest>({
      query: ({ benchmarks, year, source = 'ap' }) => ({
        url: `${import.meta.env.VITE_APP_MUTUALFUNDS_URL}/api/v2/admin/benchmark/multiple-data`,
        method: 'POST',
        params: {
          source,
        },
        body: {
          benchmarks,
          year,
        },
      }),
      transformResponse: (response: any): MultipleBenchmarkResponse => {
        return {
          statusCode: response?.statusCode || 200,
          data: response?.data || {},
          message: response?.message || 'Multiple benchmark data fetched',
          success: response?.success ?? true,
        };
      },
      transformErrorResponse: createApiErrorTransformer('PmsGetMultipleBenchmarkData', true),
      providesTags: ['Portfolio'],
    }),

    getAPPortfolioSummary: builder.query<PMSPortfolioSummaryAPResponse, { source?: string }>({
      query: ({ source = 'ap' }) => ({
        url: '/v1/ap/get-ap-portfolio-summary',
        method: 'GET',
        params: {
          source,
        },
      }),
      transformErrorResponse: createApiErrorTransformer('PmsGetClientPortfolioSummary', true),
      providesTags: ['Portfolio'],
    }),

    getPMSClientPortfolioSummary: builder.query<
      PMSPortfolioSummaryAPResponse,
      { clientCode: string; source?: string }
    >({
      query: ({ clientCode, source = 'ap' }) => ({
        url: '/v1/ap/get-ap-client-portfolio-summary',
        method: 'GET',
        params: {
          clientCode,
          source,
        },
      }),
      transformErrorResponse: createApiErrorTransformer('PmsGetClientPortfolioSummary', true),
      providesTags: ['Portfolio'],
    }),

    getClientPortfolioPerformance: builder.query<
      PMSPortfolioPerformanceResponse,
      {
        clientCode: string;
        financeYear: number | string;
        source?: string;
      }
    >({
      query: ({ clientCode, financeYear, source = 'ap' }) => ({
        url: '/v1/clients/get-client-portfolio-performance',
        method: 'GET',
        params: {
          clientCode,
          financeYear,
          source,
        },
      }),
      transformResponse: (response: any): PMSPortfolioPerformanceResponse => {
        return {
          statusCode: response?.statusCode || 200,
          data: response?.data || {},
          message: response?.message || 'Portfolio performance fetched',
          success: response?.success ?? true,
        };
      },
      transformErrorResponse: createApiErrorTransformer('PmsGetClientPortfolioPerformance', true),
      providesTags: ['Portfolio'],
    }),

    getClientTransactions: builder.query<
      PMSTransactionsPagedResponse,
      {
        clientCode: string;
        source?: string;
        pageNumber?: number;
        pageSize?: number;
        schemeName?: string;
        searchKey?: string;
      }
    >({
      query: ({
        clientCode,
        source = 'ap',
        pageNumber = 1,
        pageSize = 10,
        schemeName = 'All',
        searchKey = '',
      }) => ({
        url: '/v1/clients/get-client-transactions',
        method: 'GET',
        params: {
          clientCode,
          source,
          pageNumber,
          pageSize,
          schemeName,
          searchKey,
        },
      }),
      transformResponse: (response: any): PMSTransactionsPagedResponse => {
        return {
          statusCode: response?.statusCode || 200,
          data: {
            pageNumber: response?.data?.pageNumber || 1,
            pageSize: response?.data?.pageSize || 10,
            totalCount: response?.data?.totalCount || 0,
            totalPages: response?.data?.totalPages || 1,
            data: response?.data?.data || [],
            summary: response?.data?.summary,
          },
        };
      },
      transformErrorResponse: createApiErrorTransformer('PmsGetClientTransactions', true),
      providesTags: ['Portfolio'],
    }),

    getSchemes: builder.query<
      PMSSchemesPagedResponse,
      {
        pageNumber?: number;
        pageSize?: number;
        searchTerm?: string;
        source?: string;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
        strategy?: string;
        category?: string;
        clientCode?: string;
      }
    >({
      query: ({
        pageNumber = 1,
        pageSize = 10,
        searchTerm = '',
        source = 'ap',
        sortBy,
        sortOrder,
        strategy,
        category,
        clientCode,
      }) => {
        const params: any = {
          pageNumber,
          pageSize,
          searchTerm,
          source,
          ...(clientCode ? { clientCode } : {}),
        };

        // Add filter parameters if they exist
        if (sortBy) params.sortBy = sortBy;
        if (sortOrder) params.sortOrder = sortOrder;
        if (strategy) params.strategy = strategy;
        if (category) params.category = category;

        console.log('PMS Schemes API Request - URL: /v1/scheme/get-scheme, Params:', params);

        return {
          url: '/v1/scheme/get-scheme',
          method: 'GET',
          params,
        };
      },
      transformResponse: (response: any, meta, arg): PMSSchemesPagedResponse => {
        const { pageNumber = 1, pageSize = 10 } = arg;
        console.log('PMS Schemes API Response:', response);
        console.log('PMS Schemes API Request Args:', arg);

        const transformedResponse = {
          statusCode: response?.statusCode || 200,
          data: {
            pageNumber: response?.data?.pageNumber || pageNumber,
            pageSize: response?.data?.pageSize || pageSize,
            totalCount: response?.data?.totalCount || 0,
            totalPages: response?.data?.totalPages || 1,
            data: response?.data?.data || [],
            summary: response?.data?.summary || {
              bestPerformer: '',
              minInvestment: 0,
              totalSchemes: '0',
            },
          },
          message: response?.message || 'Schemes fetched successfully',
          success: response?.success ?? true,
        };

        console.log('PMS Schemes Transformed Response:', transformedResponse);
        return transformedResponse;
      },
      transformErrorResponse: createApiErrorTransformer('PmsGetSchemes', true),
      providesTags: ['Schemes'],
    }),

    getSchemeSuggestions: builder.query<
      PMSSchemeSuggestionsResponse,
      { amcCode?: string; categoryId?: number }
    >({
      query: ({ amcCode, categoryId }) => {
        const params: Record<string, number | string> = {};
        if (amcCode) params.amcCode = amcCode;
        if (categoryId) params.categoryId = categoryId;
        return {
          url: '/v1/scheme/get-scheme-suggestions',
          method: 'GET',
          params,
        };
      },
      transformErrorResponse: createApiErrorTransformer('PmsGetSchemeSuggestions', true),
      providesTags: ['Schemes'],
    }),

    getSchemeDetails: builder.query<PMSSchemeDetailResponse, { schemeId: number }>({
      query: ({ schemeId }) => ({
        url: '/v1/scheme/get-scheme-details',
        method: 'GET',
        params: { schemeId },
      }),
      transformErrorResponse: createApiErrorTransformer('PmsGetSchemeDetails', true),
      providesTags: ['Schemes'],
    }),

    getTopSectorHoldings: builder.query<
      TopSectorHoldingsResponse,
      { clientCode?: string; source?: string; searchTerm?: string; tag?: string }
    >({
      query: ({ clientCode, source = 'ap', searchTerm = '', tag = 'top5' }) => ({
        url: '/v1/scheme/get-scheme-top-sector-holding-data',
        method: 'GET',
        params: {
          source,
          searchTerm,
          tag,
          ...(clientCode ? { clientCode } : {}),
        },
      }),
      transformErrorResponse: createApiErrorTransformer('PmsGetTopSectorHoldings', true),
      providesTags: ['Portfolio'],
    }),

    getTopCompanyHoldings: builder.query<
      TopCompanyHoldingsResponse,
      { clientCode?: string; source?: string; searchTerm?: string; tag?: string }
    >({
      query: ({ clientCode, source = 'ap', searchTerm = '', tag = 'top5' }) => ({
        url: '/v1/scheme/get-scheme-top-company-holding-data',
        method: 'GET',
        params: {
          source,
          searchTerm,
          tag,
          ...(clientCode ? { clientCode } : {}),
        },
      }),
      transformErrorResponse: createApiErrorTransformer('PmsGetTopCompanyHoldings', true),
      providesTags: ['Portfolio'],
    }),

    getClientDashboard: builder.query<
      PMSDashboardResponse,
      { clientCode: string; source?: string }
    >({
      query: ({ clientCode, source = 'ap' }) => ({
        url: '/v1/clients/get-client-dashboard',
        method: 'GET',
        params: {
          clientCode,
          source,
        },
      }),
      transformResponse: (response: any): PMSDashboardResponse => {
        const rawData = response?.data || response;

        // Helper function to safely convert values to numbers
        const toNumber = (v: any): number => {
          if (v === null || v === undefined || v === '') return 0;
          const n = typeof v === 'string' ? Number(v.toString().replace(/[,\s]/g, '')) : Number(v);
          return isNaN(n) ? 0 : n;
        };

        // Transform sector data to chart format
        const transformToChartData = (data: any[], colors: string[]): ChartData[] => {
          if (!Array.isArray(data)) return [];
          return data.map((item, index) => {
            // Handle different possible field names for the key and value
            const name = item.Key || item.key || item.name || '';
            const averageValue = item.Average || item.average || item.value || 0;
            const numericValue = toNumber(averageValue) * 100; // Convert to percentage

            return {
              name: name,
              value: numericValue,
              percentage: numericValue,
              color: colors[index % colors.length],
            };
          });
        };

        // Transform data specifically for PortfolioAllocationChart (expects 'key' and 'average' fields)
        const transformToPortfolioChartData = (data: any[]): any[] => {
          if (!Array.isArray(data)) return [];
          return data.map((item) => {
            const key = item.Key || item.key || item.name || '';
            const average = toNumber(item.Average || item.average || item.value || 0);

            return {
              key: key,
              average: average, // Keep as decimal (0.xx format)
            };
          });
        };

        // Transform scheme holdings data to match HoldingsTable expected format
        const transformSchemeHoldings = (data: any[]): any[] => {
          if (!Array.isArray(data)) return [];
          return data.map((item) => ({
            isin: item.ISIN || item.isin || '',
            company_name: item.CompanyName || item.company_name || '',
            security_type: item.SecurityType || item.security_type || '',
            category: item.Category || item.category || '',
            sector: item.Sector || item.sector || '',
            portfolio_weightage: toNumber(item.PortfolioWeightage || item.portfolio_weightage || 0),
            portfolio_value: toNumber(item.PortfolioValue || item.portfolio_value || 0),
            holding_date: item.HoldingDate || item.holding_date || '',
            valuation_date: item.ValuationDate || item.valuation_date || '',
          }));
        };

        // Extract holdings data
        const holdings = rawData?.Profile?.Holdings || [];
        const holdingsReport = rawData?.Profile?.HoldingsReport || {};

        // Transform regular holdings to match HoldingsTable format
        const transformedHoldings = transformSchemeHoldings(holdings);

        // Get raw scheme data and transform it into the new format
        const rawSchemeData = rawData?.Profile?.Schemes || [];

        // Transform scheme data into flatter structure with normalized holdings
        const schemeHolding = rawSchemeData.map((scheme: any) => ({
          Scheme: scheme.Scheme || scheme.scheme || '',
          SchemeId: scheme.SchemeId || scheme.schemeId || '',
          holdingData: transformSchemeHoldings(scheme.SchemeHoldings || []),
        }));

        // Also create portfolio chart format for regular holdings (for pie charts)
        const sectorPortfolioData = transformToPortfolioChartData(holdingsReport.Sector || []);
        const categoryPortfolioData = transformToPortfolioChartData(holdingsReport.Category || []);
        const securityTypePortfolioData = transformToPortfolioChartData(
          holdingsReport.SecurityType || [],
        );
        const transactions = rawData?.Profile?.Transactions || [];
        const schemeIds = Array.isArray(rawData?.Profile?.Schemes)
          ? rawData.Profile.Schemes.map((s: any) => s?.SchemeId).filter(
              (id: any) => id !== undefined && id !== null,
            )
          : [];
        const schemeName = Array.isArray(rawData?.Profile?.Schemes)
          ? rawData.Profile.Schemes.map((s: any) => s?.Scheme).filter(
              (id: any) => id !== undefined && id !== null,
            )
          : [];

        // Extract scheme-level reports from the actual API response structure
        // Based on your API response: Profile.AMC[].Scheme[].SchemeHoldingsReport
        let schemeSectorRaw: any[] = [];
        let schemeCategoryRaw: any[] = [];
        let schemeSecurityRaw: any[] = [];

        const amcs = Array.isArray(rawData?.Profile?.AMC) ? rawData.Profile.AMC : [];
        // Get scheme-wise transactions
        const schemeWiseTransactions: Record<string, any[]> = {}; // key: schemeId or schemeName
        for (const amc of amcs) {
          const schemes = Array.isArray(amc?.Scheme) ? amc.Scheme : [];

          for (const scheme of schemes) {
            const schemeId =
              scheme?.SchemeId || scheme?.schemeId || scheme?.SchemeName || 'Unknown';
            const transactions = Array.isArray(scheme?.Transactions) ? scheme.Transactions : [];
            schemeWiseTransactions[schemeId] = transactions;

            if (scheme?.SchemeHoldingsReport) {
              if (Array.isArray(scheme.SchemeHoldingsReport.Sector)) {
                schemeSectorRaw.push(scheme.SchemeHoldingsReport.Sector);
              }
              // Take category data from the first scheme that has it
              if (Array.isArray(scheme.SchemeHoldingsReport.Category)) {
                schemeCategoryRaw.push(scheme.SchemeHoldingsReport.Category);
              }
              if (Array.isArray(scheme.SchemeHoldingsReport.SecurityType)) {
                schemeSecurityRaw.push(scheme.SchemeHoldingsReport.SecurityType);
              }
            }
          }
        }

        // Transform scheme holdings to match HoldingsTable format
        // const transformedSchemeHoldings = transformSchemeHoldings(schemeHoldingRaw);

        // const schemeSectorChart = transformToChartData(schemeSectorRaw || [], SECTOR_COLORS);
        const schemeSectorChart = [];
        for (const sectorData of schemeSectorRaw) {
          const sectorChartData = transformToChartData(sectorData, SECTOR_COLORS);
          schemeSectorChart.push(sectorChartData);
        }
        // const schemeCategoryChart = transformToChartData(schemeCategoryRaw || [], CATEGORY_COLORS);
        // const schemeSecurityChart = transformToChartData(schemeSecurityRaw || [], SECURITY_TYPE_COLORS);
        const schemeCategoryChart = [];
        const schemeSecurityChart = [];
        for (const categoryData of schemeCategoryRaw) {
          const categoryChartData = transformToChartData(categoryData, CATEGORY_COLORS);
          schemeCategoryChart.push(categoryChartData);
        }
        for (const securityData of schemeSecurityRaw) {
          const securityChartData = transformToChartData(securityData, SECURITY_TYPE_COLORS);
          schemeSecurityChart.push(securityChartData);
        }

        // Extract basic dashboard metrics
        const networth = rawData?.Profile?.Networth || {};
        const amcData = rawData?.Profile?.AMC || [];

        const dashboardData: PMSDashboardData = {
          currentNetworth: toNumber(
            networth.CurrentNetworth || rawData?.currentNetworth || rawData?.current_networth || 0,
          ),
          investment: toNumber(networth.Investment || rawData?.investment || 0),
          gainLoss: toNumber(networth.GainLoss || rawData?.gainLoss || rawData?.gain_loss || 0),
          xirr: toNumber(networth.Xirr || rawData?.xirr || 0),
          amcCount: amcData.length || toNumber(rawData?.amcCount || rawData?.amc_count || 0),
          // Store full data for complex operations
          fullData: rawData,
          // Chart-ready data
          schemeSectorChart,
          schemeCategoryChart,
          schemeSecurityChart,
          // Portfolio chart format
          sectorPortfolioData,
          categoryPortfolioData,
          securityTypePortfolioData,

          schemeHoldingRaw: schemeHolding, // Use transformed data
          holdings: transformedHoldings,
          transactions,
          schemeName,
          schemeIds,
          amcData,
        };

        console.log('Final dashboardData --- ', dashboardData);

        return {
          success: response?.success ?? true,
          data: dashboardData,
          message: response?.message || 'Dashboard data fetched successfully',
        };
      },
      transformErrorResponse: createApiErrorTransformer('PmsGetClientDashboard', true),
      providesTags: ['Portfolio'],
    }),
  }),
});

// Export hooks for components to use
export const {
  useGetClientPortfolioSummaryQuery,
  useLazyGetClientPortfolioSummaryQuery,
  useGetClientHoldingsQuery,
  useLazyGetClientHoldingsQuery,
  useGetClientSectorHoldingsQuery,
  useLazyGetClientSectorHoldingsQuery,
  useGetClientCategorySummaryQuery,
  useLazyGetClientCategorySummaryQuery,
  useGetSchemeBenchmarkReturnsQuery,
  useLazyGetSchemeBenchmarkReturnsQuery,
  useGetMultipleBenchmarkDataQuery,
  useLazyGetMultipleBenchmarkDataQuery,
  useGetClientPortfolioPerformanceQuery,
  useLazyGetClientPortfolioPerformanceQuery,
  useGetClientTransactionsQuery,
  useLazyGetClientTransactionsQuery,
  useGetClientDashboardQuery,
  useLazyGetClientDashboardQuery,
  useGetSchemesQuery,
  useLazyGetSchemesQuery,
  useGetSchemeDetailsQuery,
  useLazyGetSchemeDetailsQuery,
  useGetSchemeSuggestionsQuery,
  useLazyGetSchemeSuggestionsQuery,
  useGetTopSectorHoldingsQuery,
  useLazyGetTopSectorHoldingsQuery,
  useGetTopCompanyHoldingsQuery,
  useLazyGetTopCompanyHoldingsQuery,
  useGetAPPortfolioSummaryQuery,
  useLazyGetAPPortfolioSummaryQuery,
  useGetPMSClientPortfolioSummaryQuery,
  useLazyGetPMSClientPortfolioSummaryQuery,
} = pmsApi;

export default pmsApi;
